<script setup lang="ts">
import type { AddEntityType } from '~/components/orga/OrgaElement.vue'
import type { GridCol } from '~~/types/orgGrid'
import OrgaElement from '~/components/orga/OrgaElement.vue'
import OrgReorderHandle from '~/components/orga/reorder/OrgReorderHandle.vue'
import { useFormatLabel } from '~/composables/ui/useFormatLabel'

const props = defineProps<{
  col: GridCol
  colIndex: number
  directionLib: string
  isEditing: boolean
  canModifyDomaine?: boolean
  canMutateChildren?: boolean
  emphasizeScope?: boolean
  inScope?: boolean
  reorderable?: boolean
  reorderIndex?: number
  reorderTotal?: number
}>()

const emit = defineEmits<{
  (event: 'add-click', payload: { entityType: AddEntityType; context?: Record<string, string | undefined> }): void
  (event: 'modify-click', context?: Record<string, string | undefined>): void
  (event: 'mouseenter-col', col: GridCol): void
  (event: 'mouseleave-col'): void
  (event: 'reorder-move', payload: { groupId: string; keyName: string; fromIndex: number }): void
}>()

const { formatLabel } = useFormatLabel()

function onHandleMove(groupId: string, keyName: string, fromIndex: number): void {
  emit('reorder-move', { groupId, keyName, fromIndex })
}

const visibleItems = computed(() =>
  props.col.items.filter((item) => formatLabel(item?.label) !== undefined)
)
</script>

<template>
  <div
    class="col col--animate"
    :class="{
      'col--two-cols': col.items.length > 10,
      'domaine-column--in-scope': emphasizeScope && inScope,
      'domaine-column--muted': emphasizeScope && !inScope,
    }"
    :style="{ '--stagger': colIndex }"
    :data-domaine-id="col?.header?.label ?? undefined"
    :data-reorder-key="reorderable ? col?.header?.label : undefined"
    @mouseenter="emit('mouseenter-col', col)"
    @mouseleave="emit('mouseleave-col')"
  >
    <div
      class="col-header col-header--animate"
      :style="{ '--stagger': colIndex }"
    >
      <div
        class="col-header__card"
        :class="{ 'col-header__card--grip': reorderable }"
        :data-onboarding="colIndex === 0 ? 'direction-domaine-header' : undefined"
      >
        <OrgaElement
          :label="formatLabel(col?.header?.label)"
          :to="col?.header?.to"
          :lib-long="col?.header?.libLong ?? null"
          :desc="col?.header?.desc ?? null"
          header
          :entity-modify-kind="!canModifyDomaine ? undefined : 'domaine'"
          :add-entity-type="!canModifyDomaine ? undefined : 'equipeDomaine'"
          add-label="Ajouter CODIR"
          modify-kind="equipe"
          :add-context="{ directionLib: directionLib, domaineLib: col?.header?.label }"
          @add-click="emit('add-click', $event)"
          @modify-click="emit('modify-click', $event)"
        />
        <Transition name="domaine-grip">
          <span
            v-if="reorderable && col?.header?.label"
            class="col-header__grip"
            :class="{ 'col-header__grip--on-dark': Boolean(col.header.to) }"
          >
            <OrgReorderHandle
              :group-id="directionLib"
              :item-key="col.header.label"
              :label="formatLabel(col.header.label) ?? col.header.label"
              :position="(reorderIndex ?? 0) + 1"
              :total="reorderTotal ?? 1"
              axis="horizontal"
              @move="onHandleMove"
            />
          </span>
        </Transition>
      </div>
    </div>
    <div class="items-list">
      <div
        v-for="(item, itemIndex) in visibleItems"
        :key="item?.label ?? itemIndex"
        class="item item--animate"
        :style="{
          '--stagger': colIndex * 8 + itemIndex,
          '--agence-index': itemIndex,
          '--agence-count': visibleItems.length,
        }"
        :data-agence-id="item?.label ?? undefined"
      >
        <div :data-onboarding="colIndex === 0 && itemIndex === 0 ? 'direction-agence-item' : undefined">
          <OrgaElement
            :label="formatLabel(item?.label)"
            :to="item?.to"
            :lib-long="item?.libLong ?? null"
            :desc="item?.desc ?? null"
            button-type="secondary"
            :modify-kind="!canMutateChildren ? undefined : 'agence'"
            :add-context="{ directionLib: directionLib, domaineLib: col?.header?.label, ageLib: item?.label }"
            :compact-modify="col.items.length > 10"
            @modify-click="emit('modify-click', $event)"
          />
        </div>
      </div>
      <Transition name="expand-edit">
        <div
          v-if="isEditing && canMutateChildren"
          class="item item--add"
          :style="{ '--agence-index': visibleItems.length, '--agence-count': visibleItems.length }"
        >
          <OrgaElement
            status="add"
            add-entity-type="agence"
            :add-context="{ directionLib: directionLib, domaineLib: col?.header?.label }"
            @add-click="emit('add-click', $event)"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/orga/national-grid" as *;
@use "~/assets/scss/partials/orga/orga-page-animations" as motion;
@use "~/assets/scss/partials/elpdl/colors" as colors;
@use "~/assets/scss/partials/elpdl/spacing" as spacing;

@include motion.direction-page-animations;

.domaine-column--in-scope {
  outline: 1px solid color-mix(in srgb, var(--color-primary) 45%, transparent);
  outline-offset: 4px;
  border-radius: 12px;
}

.domaine-column--muted {
  opacity: 0.72;
}

.col-header__card {
  position: relative;
  min-width: 0;
}

.col-header__card :deep(.block-content) {
  transition: padding 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.col-header__card--grip :deep(.block-content) {
  padding-right: spacing.$spacing-wide;
  padding-left: calc(#{spacing.$spacing-wide} + #{spacing.$spacing-xl});
}

.col-header__grip--on-dark :deep(.org-reorder-handle) {
  color: colors.$enedis-neutral-10;
}

.col-header__grip--on-dark :deep(.org-reorder-handle:focus-visible) {
  outline-color: colors.$enedis-neutral-10;
}

.col-header__grip {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  left: spacing.$spacing-3xs;
  display: flex;
  align-items: center;
}

.domaine-grip-enter-active,
.domaine-grip-leave-active {
  transition: opacity 0.22s ease, transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.domaine-grip-enter-from,
.domaine-grip-leave-to {
  opacity: 0;
  transform: translateX(spacing.$spacing-2xs * -1);
}

@media (prefers-reduced-motion: reduce) {
  .col-header__card :deep(.block-content),
  .domaine-grip-enter-active,
  .domaine-grip-leave-active {
    transition: none;
  }
}

.item--animate {
  animation-duration: 0.2s;
  animation-delay: calc(var(--agence-index, 0) * 16ms);
}

.items-list {
  transition: gap 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.org-reorder-drag .items-list,
.org-reorder-chosen .items-list {
  gap: 0;
}

.org-reorder-drag .items-list > .item,
.org-reorder-chosen .items-list > .item {
  overflow: hidden;
  animation: agenceSlideOut 0.32s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: calc((var(--agence-count, 1) - var(--agence-index, 0)) * 28ms);
}

@keyframes agenceSlideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
    max-height: 5rem;
  }

  to {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
    max-height: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .items-list {
    transition: none;
  }

  .org-reorder-drag .items-list > .item,
  .org-reorder-chosen .items-list > .item {
    animation: none;
    opacity: 0;
    max-height: 0;
  }
}
</style>
